import React, { useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';

const AnalysisLoadingModal = ({ step }) => {
    const steps = [
        "Görüntü işleniyor...",
        "İçerik metne dönüştürülüyor...",
        "Alerjenlerinizle çapraz kontrol yapılıyor...",
        "Güvenlik analizi tamamlanıyor..."
    ];

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in">
            <div className="text-center">
                <div className="relative size-32 mx-auto mb-8">
                    <div className="absolute inset-0 border-4 border-rose-500/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-rose-500 rounded-full border-t-transparent animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="material-symbols-outlined text-5xl text-rose-500 animate-pulse">
                            {step < 2 ? 'photo_filter' : (step === 2 ? 'search_check' : 'security')}
                        </span>
                    </div>
                </div>
                <h2 className="text-2xl font-black text-white mb-4 transition-all duration-500 transform">
                    {steps[step]}
                </h2>
                <div className="flex justify-center gap-1.5">
                    {steps.map((_, idx) => (
                        <div key={idx} className={`h-1.5 rounded-full transition-all duration-500 ${idx <= step ? 'w-8 bg-rose-500' : 'w-4 bg-white/20'}`}></div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AllergenScanner = () => {
    const [isScanning, setIsScanning] = useState(false);
    const [image, setImage] = useState(null);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [analysisStep, setAnalysisStep] = useState(0);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    // Dosya (Fotoğraf) Seçme/Çekme İşlemi
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImage(reader.result);
                setIsScanning(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Kamera açma
    const startCamera = async () => {
        try {
            setImage(null);
            setIsScanning(true);
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Kamera açılamadı:", err);
            // alert("Canlı kamera erişimi sağlanamadı. Fotoğraf çekme moduna yönlendiriliyorsunuz.");
            setIsScanning(false);
            triggerFileInput(); // Fallback to file input if camera fails
        }
    };

    // Fotoğraf çekme
    const captureImage = useCallback(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            const context = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

            const dataUrl = canvas.toDataURL('image/jpeg');
            setImage(dataUrl);

            // Stop streams
            if (video.srcObject) {
                const tracks = video.srcObject.getTracks();
                tracks.forEach(track => track.stop());
            }
            setIsScanning(false);
        }
    }, [videoRef]);

    // Analiz için gönder
    const analyzeImage = async () => {
        if (!image) return;

        setLoading(true);
        setAnalysisStep(0);
        setAnalysisResult(null);

        try {
            const authToken = localStorage.getItem('authToken');
            const { createWorker } = await import('tesseract.js');

            // Step 0: Processing
            setAnalysisStep(0);
            await new Promise(r => setTimeout(r, 600));

            // Step 1: Client-Side OCR
            setAnalysisStep(1);
            let ocrText = null;
            try {
                const worker = await createWorker('tur');
                const ret = await worker.recognize(image);
                ocrText = ret.data.text;
                await worker.terminate();
                console.log("Local OCR Success:", ocrText);
            } catch (err) {
                console.warn("Local OCR Failed, falling back to AI:", err);
            }

            // Step 2: Backend Brain / Fallback AI
            setAnalysisStep(2);
            let response;

            // If local OCR found enough text, use the cheaper endpoint
            if (ocrText && ocrText.trim().length > 15) {
                response = await fetch(`${API_BASE_URL}/api/scan/analyze-text`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({ text: ocrText })
                });
            } else {
                // Fallback to Server-Side AI Vision
                response = await fetch(`${API_BASE_URL}/api/scan/analyze`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({ image: image })
                });
            }

            const data = await response.json();

            // Step 3: Final Security Analysis
            setAnalysisStep(3);
            await new Promise(r => setTimeout(r, 500));

            setAnalysisResult(data);
        } catch (err) {
            console.error("Analiz hatası:", err);
            alert("Analiz sırasında bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const resetScan = () => {
        setImage(null);
        setAnalysisResult(null);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0b0b0b] p-4 pb-24 md:pb-8">
            {loading && <AnalysisLoadingModal step={analysisStep} />}
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-6 px-1">
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-3xl text-rose-500">Document_Scanner</span>
                        Alerjen Kontrol
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                        Ürün içeriğini tarat, senin için riskli mi öğren.
                    </p>
                </div>

                {/* Main Content */}
                <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800">

                    {/* Hidden File Input for Native Camera */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                    />

                    {/* Camera/Image View */}
                    <div className="relative aspect-[4/3] bg-black flex items-center justify-center">
                        {!image && !isScanning && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-8 w-full">
                                <button
                                    onClick={triggerFileInput}
                                    className="flex flex-col items-center gap-4 p-8 bg-rose-500 rounded-2xl text-white hover:bg-rose-600 transition-all active:scale-95 group"
                                >
                                    <div className="size-16 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm border border-white/20 group-hover:scale-110 transition-transform">
                                        <span className="material-symbols-outlined text-4xl">photo_camera</span>
                                    </div>
                                    <div className="text-center">
                                        <span className="font-black block text-lg">Fotoğraf Çek</span>
                                        <span className="text-[10px] opacity-80">En güvenli ve kolay yöntem</span>
                                    </div>
                                </button>

                                <button
                                    onClick={startCamera}
                                    className="flex flex-col items-center gap-4 p-8 bg-gray-100 dark:bg-gray-800 rounded-2xl text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-all active:scale-95 group"
                                >
                                    <div className="size-16 rounded-full bg-white/50 dark:bg-black/20 flex items-center justify-center backdrop-blur-sm border border-gray-200 dark:border-white/10 group-hover:scale-110 transition-transform">
                                        <span className="material-symbols-outlined text-4xl">video_camera_front</span>
                                    </div>
                                    <div className="text-center">
                                        <span className="font-black block text-lg">Canlı Tarama</span>
                                        <span className="text-[10px] opacity-60">HTTPS gerektirir</span>
                                    </div>
                                </button>
                            </div>
                        )}

                        {isScanning && (
                            <div className="relative w-full h-full">
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 border-[30px] border-black/40 pointer-events-none flex items-center justify-center">
                                    <div className="size-full border-2 border-dashed border-rose-500/50 rounded-xl relative">
                                        <div className="absolute top-0 left-0 size-8 border-t-4 border-l-4 border-rose-500 -mt-1 -ml-1"></div>
                                        <div className="absolute top-0 right-0 size-8 border-t-4 border-r-4 border-rose-500 -mt-1 -mr-1"></div>
                                        <div className="absolute bottom-0 left-0 size-8 border-b-4 border-l-4 border-rose-500 -mb-1 -ml-1"></div>
                                        <div className="absolute bottom-0 right-0 size-8 border-b-4 border-r-4 border-rose-500 -mb-1 -mr-1"></div>
                                    </div>
                                </div>
                                <div className="absolute bottom-10 left-0 right-0 flex justify-center items-center gap-8 px-8">
                                    <button
                                        onClick={() => {
                                            const stream = videoRef.current?.srcObject;
                                            if (stream) stream.getTracks().forEach(t => t.stop());
                                            setIsScanning(false);
                                        }}
                                        className="size-12 bg-white/20 backdrop-blur-md rounded-full text-white flex items-center justify-center border border-white/30"
                                    >
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                    <button
                                        onClick={captureImage}
                                        className="size-20 bg-white rounded-full border-8 border-rose-500/30 flex items-center justify-center active:scale-90 transition-transform shadow-2xl"
                                    >
                                        <div className="size-14 bg-rose-500 rounded-full"></div>
                                    </button>
                                    <div className="size-12"></div> {/* Spacer */}
                                </div>
                            </div>
                        )}

                        {image && (
                            <div className="relative w-full h-full bg-gray-100 dark:bg-black">
                                <img src={image} alt="Captured" className="w-full h-full object-contain" />
                                <button
                                    onClick={resetScan}
                                    className="absolute top-4 right-4 size-10 bg-black/50 backdrop-blur-md rounded-full text-white flex items-center justify-center border border-white/20 active:scale-95 transition-transform"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        )}

                        {/* Hidden Canvas for Capture */}
                        <canvas ref={canvasRef} className="hidden" />
                    </div>

                    {/* Controls */}
                    <div className="p-6 border-t border-gray-100 dark:border-gray-800">
                        {image && !analysisResult && (
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setImage(null)}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 transition-colors"
                                >
                                    Tekrar Çek
                                </button>
                                <button
                                    onClick={analyzeImage}
                                    disabled={loading}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold bg-rose-500 text-white hover:bg-rose-600 transition-colors shadow-lg shadow-rose-500/30 flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                            Analiz Ediliyor...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined">analytics</span>
                                            Analiz Et
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Results Modal */}
                {analysisResult && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                        <div className="relative bg-white dark:bg-[#1a1a1a] w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
                            {/* Close Button */}
                            <button
                                onClick={resetScan}
                                className="absolute top-4 right-4 z-10 size-10 flex items-center justify-center rounded-full bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 transition-colors"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                            <div className={`p-6 ${analysisResult.risks.some(r => r.severity === "Red Flag")
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : analysisResult.risks.length > 0 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-green-50 dark:bg-green-900/20'
                                } text-center`}>
                                <div className={`size-16 mx-auto rounded-full flex items-center justify-center mb-4 ${analysisResult.risks.some(r => r.severity === "Red Flag")
                                    ? 'bg-red-100 text-red-600'
                                    : analysisResult.risks.length > 0 ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
                                    }`}>
                                    <span className="material-symbols-outlined text-4xl">
                                        {analysisResult.error ? 'error' : (
                                            analysisResult.risks.some(r => r.severity === "Red Flag") ? 'dangerous' : (analysisResult.risks.length > 0 ? 'warning' : 'check_circle')
                                        )}
                                    </span>
                                </div>
                                <h2 className="text-xl font-black text-gray-900 dark:text-white mb-1">
                                    {analysisResult.error ? 'Analiz Yapılamadı' : (
                                        analysisResult.risks.some(r => r.severity === "Red Flag")
                                            ? 'Tehlike: Alerjen Var!'
                                            : analysisResult.risks.length > 0 ? 'Uyarı: Risk Tespit Edildi' : 'Temiz Görünüyor'
                                    )}
                                </h2>
                                {analysisResult.usedMasterData ? (
                                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-bold uppercase tracking-wider mb-2">
                                        <span className="material-symbols-outlined text-[12px]">verified</span>
                                        Resmi Doğrulanmış Veri
                                    </div>
                                ) : (
                                    analysisResult.isCommunityVerified ? (
                                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-bold uppercase tracking-wider mb-2">
                                            <span className="material-symbols-outlined text-[12px]">group</span>
                                            Topluluk Onaylı ({analysisResult.verificationCount}/3+)
                                        </div>
                                    ) : (
                                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider mb-2">
                                            <span className="material-symbols-outlined text-[12px]">clinical_notes</span>
                                            Doğrulama Bekleniyor ({analysisResult.verificationCount || 1}/3)
                                        </div>
                                    )
                                )}
                                <p className="text-sm font-medium opacity-80">
                                    {analysisResult.error
                                        ? analysisResult.error
                                        : (analysisResult.risks.some(r => r.severity === "Red Flag")
                                            ? 'Bu ürün senin için yüksek riskli maddeler barındırıyor.'
                                            : analysisResult.risks.length > 0
                                                ? 'Bu üründe düşük/orta riskli (eser miktar) maddeler bulundu.'
                                                : 'Taranan içerikte bilinen alerjenlerine rastlanmadı.')}
                                </p>
                            </div>

                            <div className="p-6">
                                {/* Detected Text */}
                                <div className="mb-6">
                                    <h3 className="text-xs font-bold uppercase text-gray-400 mb-2 tracking-wider">Algılanan İçerik</h3>
                                    <div className="p-3 bg-gray-50 dark:bg-black/30 rounded-xl text-xs text-gray-600 dark:text-gray-400 font-mono leading-relaxed max-h-32 overflow-y-auto">
                                        {analysisResult.text}
                                    </div>
                                </div>

                                {/* Risk Details */}
                                {analysisResult.risks && analysisResult.risks.length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-xs font-bold uppercase text-gray-400 mb-2 tracking-wider">Tespit Edilen Maddeler</h3>
                                        <div className="space-y-2">
                                            {analysisResult.risks.map((risk, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`flex items-center gap-3 p-3 rounded-xl border ${risk.severity === "Red Flag"
                                                        ? 'bg-red-50 border-red-100 text-red-700 dark:bg-red-950/20 dark:border-red-900/30'
                                                        : 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/20 dark:border-amber-900/30'
                                                        }`}
                                                >
                                                    <span className="text-xl">
                                                        {risk.severity === "Red Flag" ? '🛑' : '⚠️'}
                                                    </span>
                                                    <div className="flex-1">
                                                        <p className="font-bold text-sm leading-tight">{risk.message}</p>
                                                        <p className="text-[10px] opacity-70 uppercase font-black mt-0.5">
                                                            {risk.severity === "Red Flag" ? 'Yüksek Risk / İçerikte Var' : 'Orta Risk / Eser Miktarda'}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={resetScan}
                                    className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold hover:opacity-90 transition-opacity"
                                >
                                    Yeni Tarama Yap
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllergenScanner;
